import React, { useState, useRef, useEffect } from 'react';
import { getCardImageUrl } from '../utils/cardImageUrl';
import './CreatorCard.css';

function CreatorCard({ creator, onClick, onAddToCampaign, onToggleSelect, isSelected, actionLabel, onAction, onRemove, isFavourite, onToggleFavourite, imagePriority = false }) {
  const cardRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Only load image when card is in (or near) viewport — keeps initial load fast
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { rootMargin: '100px', threshold: 0.01 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Ensure we have required fields with fallbacks
  const safeCreator = {
    name: creator?.name || 'Unknown Creator',
    handle: creator?.handle || '@unknown',
    image: creator?.image || creator?.profileImage || creator?.profileImagePath || '/images/profile-placeholder.svg',
    age: creator?.age || 'N/A',
    niche: creator?.niche || 'General',
    specialties: creator?.specialties || [],
    bio: creator?.bio || 'Content creator passionate about creating engaging content.',
    hobbies: creator?.hobbies || 'Various interests',
    cardImagePosition: creator?.cardImagePosition || { x: 0, y: 0 },
    cardImageScale: creator?.cardImageScale || 1,
    profileImageAlignment: creator?.profileImageAlignment || 'center',
    ...creator
  };

  return (
    <div
      ref={cardRef}
      className="creator-card-unique"
      onClick={onClick}
      role="button"
      tabIndex={0}
      style={{ cursor: 'pointer' }}
    >
      <div className="main-image-wrapper-unique">
        <div className={`creator-image-clip-mask-unique${!imageLoaded ? ' creator-image-loading-unique' : ''}`}>
          {inView ? (
            <img
              className="creator-main-image-unique"
              src={getCardImageUrl(safeCreator.image)}
              alt={safeCreator.name}
              loading={imagePriority ? 'eager' : 'lazy'}
              fetchPriority={imagePriority ? 'high' : 'low'}
              decoding="async"
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                setImageLoaded(true);
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #94a3b8; font-size: 0.875rem;">No image</div>';
              }}
              style={{
                opacity: imageLoaded ? 1 : 0,
                transform: `translateY(${safeCreator.cardImagePosition?.y || 0}px) scale(${(safeCreator.cardImageScale || 1) * 0.85})`,
                transformOrigin: 'center center',
                objectPosition: 'center 25%'
              }}
            />
          ) : null}
        </div>
      </div>
      <div className="creator-info-unique">
        <div className="creator-name-section-unique">
          <div className="creator-name-row-unique">
            <h3>{(() => {
              const firstName = safeCreator.name?.split(' ')[0] || safeCreator.name || '';
              return firstName.length > 12 ? firstName.substring(0, 12) + '...' : firstName;
            })()}</h3>
            {safeCreator.tier && (
              <div className={`creator-badge-inline-unique ${safeCreator.tier === 'Pro' ? 'pro-badge-unique' : 'rising-star-badge-unique'}`}>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <path d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z" fill="currentColor"/>
                </svg>
                {safeCreator.tier === 'Pro' ? 'Pro' : 'Rising Star'}
              </div>
            )}
          </div>
          <span className="creator-handle-unique">{safeCreator.handle}</span>
        </div>
        <div className="creator-age-unique">
          <span className="card-age-text-unique">{safeCreator.age}</span>
        </div>
        <div className="creator-tags-unique">
          {safeCreator.niche && safeCreator.niche.split('.').map((niche, index) => (
            <span key={index} className="creator-tag-unique niche-tag-unique">
              {niche.trim()}
            </span>
          ))}
        </div>
        <div className="creator-specialties-unique">
          {safeCreator.specialties && safeCreator.specialties.slice(0, 2).map((specialty, index) => (
            <span key={index} className="creator-tag-unique specialty-tag-unique">
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z" fill="currentColor"/>
              </svg>
              {specialty}
            </span>
          ))}
        </div>
        
        {/* Creator location */}
        {safeCreator.location && safeCreator.location !== 'Location not specified' && (
          <div className="creator-location-unique">📍 {safeCreator.location}</div>
        )}
        
        <p className="creator-hobbies-unique">Other hobbies: {safeCreator.hobbies}</p>
      </div>
      <div className="creator-actions-unique">
        <div className="creator-icons-unique">
          <button
            className="icon-btn-unique"
            onClick={e => {
              e.stopPropagation();
              onToggleFavourite && onToggleFavourite(safeCreator);
            }}
            aria-label={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
          >
            {isFavourite ? '❤️' : '🤍'}
          </button>
          <button
            className="icon-btn-unique"
            onClick={e => {
              e.stopPropagation();
              onToggleSelect && onToggleSelect(safeCreator);
            }}
            aria-label={isSelected ? 'Deselect creator' : 'Select creator for campaign'}
          >
            {isSelected ? '✔️' : '➕'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {onRemove && (
            <button
              className="remove-btn-unique"
              onClick={e => {
                e.stopPropagation();
                onRemove(safeCreator);
              }}
            >
              Remove
            </button>
          )}
          <button
            className="campaign-btn-unique"
            onClick={e => {
              e.stopPropagation();
              (onAction || onAddToCampaign)?.(safeCreator);
            }}
          >
            {actionLabel || 'Assign Now'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreatorCard;